import useSWR from "swr";
import { instance } from "@/frontend/lib/hc";
import { useEffect } from "react";
import type { OnboardingProgress } from "@/shared/onboarding-progress";

interface Conversation {
  conversationId: string;
  conversationName: string;
  conversationType: string;
  labels: string[];
  labelTimeSpent: Record<string, number>; // milliseconds for each label
  lastUpdated: string;
  ownerPhone: string | null;
  ownerName: string | null;
  ownerJobTitle: string | null;
  messageCount: number;
  participantCount: number;
  alertsCount: number;
  alertNames: string[];
}

interface ConversationsResponse {
  conversations: Conversation[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

interface UserLabelBreakdown {
  phone: string;
  name: string;
  jobTitle: string;
  labelCounts: Record<string, number>;
  totalConversations: number;
}

interface UserLabelBreakdownResponse {
  users: UserLabelBreakdown[];
  labels: string[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

const BASE_URL = "/api/dashboard";

export interface BusinessOverview {
  leads: { total: number; active: number; created: number; previousCreated: number };
  tasks: { total: number; open: number; urgent: number; completed: number; previousCompleted: number; overdue: number };
  campaigns: {
    total: number;
    active: number;
    responses: number;
    previousResponses: number;
    failed: number;
  };
  flows: { total: number; successful: number; previousSuccessful: number; errors: number };
  autopilot: {
    enabled: number;
    handled: number;
    previousHandled: number;
    messagesSent: number;
    previousMessagesSent: number;
    escalations: number;
  };
  leadEngagement: Array<{
    stageId: number;
    name: string;
    color: string;
    leadCount: number;
    averageMessages: number;
  }>;
}

export interface ConnectedChannel {
  id: string;
  type: "desktop" | "whatsapp" | "waba" | "instagram" | "web";
  label: string;
  name: string | null;
  identifier: string;
  settingsSection: string;
  lastMessageAt: string | null;
  direction: "sent" | "received" | "synced" | null;
}

export interface DisconnectedChannel {
  id: string;
  type: "whatsapp" | "waba" | "instagram";
  label: string;
  ownerUserId?: string;
  name: string | null;
  identifier: string | null;
  settingsSection: string;
  disconnectedAt: string;
}

export interface ChannelHealthResponse {
  organizationId: string;
  channels: DisconnectedChannel[];
}

export interface DashboardNarrative {
  brief: string;
  messageVolumeAnomaly: {
    direction: "up" | "down";
    text: string;
  } | null;
  messageVolumeAnalysis: "complete" | "insufficient" | "unavailable";
  insights: Array<{
    product: "leads" | "campaigns";
    headline: string;
    detail: string;
  }>;
  generatedBy: "ai" | "formula";
}

// Fetcher function for SWR
const fetcher = async <T,>(url: string): Promise<T> => {
  return instance.get(url).json<T>();
};

export function useBusinessOverview(params?: {
  organizationId?: string;
  dateFrom?: string;
  dateTo?: string;
  compareDateFrom?: string;
  compareDateTo?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.dateFrom) searchParams.set("dateFrom", params.dateFrom);
  if (params?.dateTo) searchParams.set("dateTo", params.dateTo);
  if (params?.compareDateFrom) searchParams.set("compareDateFrom", params.compareDateFrom);
  if (params?.compareDateTo) searchParams.set("compareDateTo", params.compareDateTo);
  if (params?.organizationId) searchParams.set("organizationId", params.organizationId);
  searchParams.set("timezone", Intl.DateTimeFormat().resolvedOptions().timeZone);
  const url = params?.organizationId
    ? `${BASE_URL}/business-overview?${searchParams.toString()}`
    : null;

  return useSWR<BusinessOverview>(url, fetcher<BusinessOverview>, {
    revalidateOnFocus: false,
    dedupingInterval: 30000,
  });
}

export function useConnectedChannels(organizationId?: string) {
  const url = organizationId
    ? `${BASE_URL}/connected-channels?organizationId=${encodeURIComponent(organizationId)}`
    : null;
  return useSWR<{ channels: ConnectedChannel[] }>(
    url,
    fetcher<{ channels: ConnectedChannel[] }>,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60000,
    },
  );
}

export function useChannelHealth(organizationId?: string) {
  const url = organizationId
    ? `${BASE_URL}/channel-health?organizationId=${encodeURIComponent(organizationId)}`
    : null;
  return useSWR<ChannelHealthResponse>(
    url,
    fetcher<ChannelHealthResponse>,
    {
      revalidateOnFocus: true,
      dedupingInterval: 30000,
      refreshInterval: 30000,
    },
  );
}

export function useOnboardingProgress(organizationId?: string) {
  const url = organizationId
    ? `${BASE_URL}/onboarding-progress?organizationId=${encodeURIComponent(organizationId)}`
    : null;
  return useSWR<OnboardingProgress>(url, fetcher<OnboardingProgress>, {
    revalidateOnFocus: true,
    dedupingInterval: 30000,
  });
}

export function useDashboardNarrative(params?: {
  organizationId?: string;
  conversations?: { current: number; percentChange: number };
  overview?: BusinessOverview;
  period?: { from: string; to: string };
  messageVolume?: {
    current: Array<{ date: string; count: number }>;
    previous: Array<{ date: string; count: number }>;
    currentPeriod: { from: string; to: string };
    previousPeriod: { from: string; to: string };
    observedDate: string;
  };
}) {
  const cacheKey = params?.organizationId && params.period
    ? `dashboard-narrative-cache:${params.organizationId}:${params.period.from}:${params.period.to}`
    : null;
  const { data: cachedNarrative, mutate: cacheNarrative } = useSWR<DashboardNarrative>(cacheKey, null);
  const payload = params?.organizationId && params.overview && params.conversations && params.messageVolume
    ? {
        organizationId: params.organizationId,
        conversations: params.conversations,
        overview: params.overview,
        period: params.period,
        messageVolume: params.messageVolume,
      }
    : null;

  const result = useSWR<DashboardNarrative>(
    payload
      ? [
          `${BASE_URL}/narrative?organizationId=${encodeURIComponent(
            payload.organizationId,
          )}`,
          payload,
        ]
      : null,
    ([url, body]: [string, typeof payload]) =>
      instance.post(url, { json: body }).json<DashboardNarrative>(),
    {
      revalidateOnFocus: false,
      dedupingInterval: 300000,
    },
  );

  useEffect(() => {
    if (cacheKey && result.data && result.data !== cachedNarrative) {
      void cacheNarrative(result.data, false);
    }
  }, [cacheKey, cacheNarrative, cachedNarrative, result.data]);

  const data = result.data ?? cachedNarrative;
  return { ...result, data, isLoading: !data && result.isLoading };
}

/**
 * Hook for fetching conversations list
 */
export function useConversations(params?: {
  organizationId?: string;
  page?: number;
  pageSize?: number;
  recentOnly?: boolean;
  sortField?: string;
  sortDirection?: string;
  search?: string;
  owner?: string;
  dateFrom?: string;
  dateTo?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.organizationId) searchParams.set("organizationId", params.organizationId);
  if (params?.page) searchParams.set("page", params.page.toString());
  if (params?.pageSize) searchParams.set("pageSize", params.pageSize.toString());
  if (params?.recentOnly) searchParams.set("recentOnly", "true");
  // Always send sortDirection, sortField will default to "lastUpdated" on backend if not provided
  if (params?.sortDirection) searchParams.set("sortDirection", params.sortDirection);
  if (params?.sortField) searchParams.set("sortField", params.sortField);
  if (params?.search) searchParams.set("search", params.search);
  if (params?.owner) searchParams.set("owner", params.owner);
  if (params?.dateFrom) searchParams.set("dateFrom", params.dateFrom);
  if (params?.dateTo) searchParams.set("dateTo", params.dateTo);

  const url = params?.organizationId
    ? `${BASE_URL}/conversations?${searchParams.toString()}`
    : null;

  return useSWR<ConversationsResponse>(url, fetcher<ConversationsResponse>, {
    revalidateOnFocus: false,
    dedupingInterval: 0, // Disable caching - always fetch fresh data
  });
}

/**
 * Hook for fetching user-label breakdown
 */
export function useUserLabelBreakdown(params?: {
  organizationId?: string;
  page?: number;
  pageSize?: number;
}) {
  const searchParams = new URLSearchParams();
  if (params?.organizationId) searchParams.set("organizationId", params.organizationId);
  if (params?.page) searchParams.set("page", params.page.toString());
  if (params?.pageSize) searchParams.set("pageSize", params.pageSize.toString());

  const url = params?.organizationId
    ? `${BASE_URL}/user-label-breakdown?${searchParams.toString()}`
    : null;

  return useSWR<UserLabelBreakdownResponse>(
    url,
    fetcher<UserLabelBreakdownResponse>,
    {
    revalidateOnFocus: false,
    dedupingInterval: 30000,
    }
  );
}
